import React, { useState, useEffect } from 'react';
import { useAuth } from '../services/AuthContext';
import { collection, query, where, orderBy, getDocs, addDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';

const FilterMenu = ({
  isOpen,
  onClose,
  columns,
  currentFilters,
  onApplyFilters,
  onClearFilters,
  onSavePreset,
  presets,
  onLoadPreset,
  onDeletePreset
}) => {
  const { currentUser, db } = useAuth();
  const [filters, setFilters] = useState({});
  const [activeTab, setActiveTab] = useState('basic');
  const [showPresets, setShowPresets] = useState(false);

  // Saved symbol sets (Firestore)
  const [symbolSets, setSymbolSets] = useState([]);
  const [symbolSetName, setSymbolSetName] = useState('');

  // Initialize filters from current filters
  useEffect(() => {
    setFilters(currentFilters || {});
  }, [currentFilters]);

  // Load saved symbol sets from Firestore
  useEffect(() => {
    const loadSymbolSets = async () => {
      if (!currentUser || !db) {
        setSymbolSets([]);
        return;
      }
      try {
        const q = query(
          collection(db, 'userSymbolSets'),
          where('uid', '==', currentUser.uid),
          orderBy('updatedAt', 'desc')
        );
        const snap = await getDocs(q);
        const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setSymbolSets(rows);
      } catch (e) {
        console.error('Failed to load symbol sets:', e);
      }
    };

    loadSymbolSets();
  }, [currentUser, db]);

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Handle range filter change
  const handleRangeFilterChange = (key, index, value) => {
    const rangeValues = [...(filters[key] || [0, 100])];
    rangeValues[index] = parseFloat(value);
    
    setFilters(prev => ({
      ...prev,
      [key]: rangeValues
    }));
  };

  // Apply filters
  const handleApplyFilters = () => {
    // Remove empty filters
    const cleanedFilters = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        if (Array.isArray(value) && value.every(v => v === 0 || v === 100)) {
          // Skip default range values
          return acc;
        }
        acc[key] = value;
      }
      return acc;
    }, {});
    
    onApplyFilters(cleanedFilters);
  };

  // Clear filters
  const handleClearFilters = () => {
    setFilters({});
    onClearFilters();
  };

  // Group columns by category
  const getFilterGroups = () => {
    const groups = {
      basic: [
        'symbol_list',
        'companyName',
        'sector',
        'industry',
        'signal_score',
        'trend_state',
        'new_trend',
        'pcnt_upside_in_range_6m',
        'pcnt_upside_in_range_12m',
        'is_etf'
      ],
      technical: [
        'rsi',
        'sma_50_200',
        'sma50_slope',
        'sma200_slope',
        'z_score_200d',
        'z_score_60d',
        'z_score_21d'
      ],
      financial: [
        'returnOnEquityTTM',
        'priceSalesRatioTTM',
        'priceEarningsRatioTTM',
        'priceEarningsToGrowthRatioTTM',
        'grossProfitMarginTTM',
        'netProfitMarginTTM',
        'debtRatioTTM',
        'piotroskiScore'
      ],
      growth: [
        'revenue_1yr_growth',
        'revenue_3yr_growth',
        'forecast_next_year_revenue_growth_rate',
        'gross_profit_1yr_growth',
        'gross_profit_3yr_growth',
        'forecast_next_year_profit_growth_rate',
        'eps_cagr',
        'dcf',
        'rule_of_40'
      ]
    };

    return groups;
  };

  // Get filter groups
  const filterGroups = getFilterGroups();

  // Render filter input based on column name and type
  const renderFilterInput = (column) => {
    // Determine input type based on column name
    if (column === 'symbol_list') {
      const canSave = Boolean(currentUser && db);

      const saveSet = async () => {
        if (!canSave) {
          toast.error('You must be logged in to save symbol sets');
          return;
        }
        const raw = String(filters.symbol || '').trim();
        if (!raw) {
          toast.error('Enter at least one symbol first');
          return;
        }
        const name = String(symbolSetName || '').trim();
        if (!name) {
          toast.error('Enter a name for this list');
          return;
        }
        const symbols = raw
          .replace(/[\s\t;]+/g, ',')
          .split(',')
          .map(s => s.trim().toUpperCase())
          .filter(Boolean);
        try {
          await addDoc(collection(db, 'userSymbolSets'), {
            uid: currentUser.uid,
            name,
            symbols,
            updatedAt: serverTimestamp()
          });
          setSymbolSetName('');
          // refresh list
          const q = query(
            collection(db, 'userSymbolSets'),
            where('uid', '==', currentUser.uid),
            orderBy('updatedAt', 'desc')
          );
          const snap = await getDocs(q);
          setSymbolSets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
          toast.success('Saved symbol list');
        } catch (e) {
          console.error('Failed saving symbol set:', e);
          toast.error('Failed to save symbol list');
        }
      };

      const applySet = (setRow) => {
        const symbols = Array.isArray(setRow.symbols) ? setRow.symbols : [];
        const nextFilters = { ...filters, symbol: symbols.join(', ') };
        setFilters(nextFilters);

        // Apply immediately (load + apply)
        const cleanedFilters = Object.entries(nextFilters).reduce((acc, [key, value]) => {
          if (value !== '' && value !== null && value !== undefined) {
            if (Array.isArray(value) && value.every(v => v === 0 || v === 100)) {
              // Skip default range values
              return acc;
            }
            acc[key] = value;
          }
          return acc;
        }, {});

        onApplyFilters(cleanedFilters);
      };

      const deleteSet = async (id) => {
        if (!canSave) return;
        try {
          await deleteDoc(doc(db, 'userSymbolSets', id));
          setSymbolSets(prev => prev.filter(s => s.id !== id));
          toast.success('Deleted symbol list');
        } catch (e) {
          console.error('Failed deleting symbol set:', e);
          toast.error('Failed to delete symbol list');
        }
      };

      return (
        <div className="space-y-3">
          <input
            type="text"
            className="form-input"
            placeholder="Enter symbols (comma-separated): AAPL, MSFT, VOO"
            value={filters.symbol || ''}
            onChange={(e) => handleFilterChange('symbol', e.target.value)}
          />

          <div className="text-xs text-gray-500">
            Tip: This works alongside other filters. Use Apply to run the combined filter set.
          </div>

          <div className="border-t border-gray-200 pt-3">
            <div className="text-sm font-semibold text-gray-800 mb-2">Saved Symbol Lists</div>

            <div className="flex gap-2 mb-2">
              <input
                type="text"
                className="form-input"
                placeholder={canSave ? 'Name this list (e.g. Watchlist)' : 'Login to save lists'}
                value={symbolSetName}
                onChange={(e) => setSymbolSetName(e.target.value)}
                disabled={!canSave}
              />
              <button
                type="button"
                className={`px-3 py-2 rounded-md text-sm font-medium ${canSave ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`}
                onClick={saveSet}
                disabled={!canSave}
              >
                Save
              </button>
            </div>

            {symbolSets.length === 0 ? (
              <div className="text-sm text-gray-500">No saved lists yet.</div>
            ) : (
              <div className="space-y-2">
                {symbolSets.map((s) => (
                  <div key={s.id} className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-md">
                    <button
                      type="button"
                      className="text-left text-sm font-medium text-blue-700 hover:text-blue-900"
                      onClick={() => applySet(s)}
                      title="Load this list into the Symbols box"
                    >
                      {s.name}
                      <span className="ml-2 text-xs text-gray-500">({Array.isArray(s.symbols) ? s.symbols.length : 0})</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteSet(s.id)}
                      className="text-red-600 hover:text-red-800"
                      title="Delete"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    if (column === 'signal_score') {
      // Score filter
      return (
        <select
          className="form-select"
          value={filters[column] || ''}
          onChange={(e) => handleFilterChange(column, e.target.value)}
        >
          <option value="">Any</option>
          <option value="gte_3">3 or greater</option>
          <option value="lt_3">less than 3</option>
        </select>
      );
    } else if (column === 'trend_state') {
      // Trend state filter (UpTrend/DownTrend/Compound)
      return (
        <select
          className="form-select"
          value={filters[column] || ''}
          onChange={(e) => handleFilterChange(column, e.target.value)}
        >
          <option value="">Any</option>
          <option value="1">UpTrend</option>
          <option value="0">DownTrend</option>
          <option value="up_turning_down">Up, turning Down</option>
          <option value="down_turning_up">Down, turning Up</option>
        </select>
      );
    } else if (column === 'new_trend') {
      // New Trend dropdown filter
      return (
        <select
          className="form-select"
          value={filters[column] || ''}
          onChange={(e) => handleFilterChange(column, e.target.value)}
        >
          <option value="">All</option>
          <option value="1">New in last 1 day</option>
          <option value="3">New in last 3 days</option>
          <option value="5">New in last 5 days</option>
          <option value="10">New in last 10 days</option>
        </select>
      );
    } else if (['pcnt_upside_in_range_6m', 'pcnt_upside_in_range_12m'].includes(column)) {
      // Percent Upside in Range filter
      return (
        <select
          className="form-select"
          value={filters[column] || ''}
          onChange={(e) => handleFilterChange(column, e.target.value)}
        >
          <option value="">All</option>
          <option value="10">upside &gt; 10%</option>
          <option value="20">upside &gt; 20%</option>
          <option value="30">upside &gt; 30%</option>
          <option value="40">upside &gt; 40%</option>
          <option value="50">upside &gt; 50%</option>
        </select>
      );
    } else if (['sector', 'industry'].includes(column)) {
      // Sector/Industry filter (dropdown with common values)
      const options = column === 'sector' 
        ? ['Technology', 'Financial Services', 'Healthcare', 'Consumer Cyclical', 'Energy', 'Basic Materials', 'Utilities']
        : ['Software', 'Asset Management', 'Medical Devices', 'Oil & Gas', 'Semiconductors', 'Retail'];
      
      return (
        <select
          className="form-select"
          value={filters[column] || ''}
          onChange={(e) => handleFilterChange(column, e.target.value)}
        >
          <option value="">Any</option>
          {options.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    } else if (['z_score_200d', 'z_score_60d', 'z_score_21d'].includes(column)) {
      // Z-Score dropdown filter
      return (
        <select
          className="form-select"
          value={filters[column] || ''}
          onChange={(e) => handleFilterChange(column, e.target.value)}
        >
          <option value="">Any</option>
          <option value="gt_3">above 3</option>
          <option value="gt_2">above 2</option>
          <option value="gt_1">above 1</option>
          <option value="gt_0">above 0</option>
          <option value="lt_0">below 0</option>
          <option value="lt_-1">below -1</option>
          <option value="lt_-2">below -2</option>
          <option value="lt_-3">below -3</option>
        </select>
      );
    } else if (column === 'rsi') {
      // RSI dropdown filter
      return (
        <select
          className="form-select"
          value={filters[column] || ''}
          onChange={(e) => handleFilterChange(column, e.target.value)}
        >
          <option value="">Any</option>
          <option value="gt_80">&gt; 80</option>
          <option value="gt_70">&gt; 70</option>
          <option value="gt_60">&gt; 60</option>
          <option value="gt_50">&gt; 50</option>
          <option value="lt_50">&lt; 50</option>
          <option value="lt_40">&lt; 40</option>
          <option value="lt_30">&lt; 30</option>
          <option value="lt_20">&lt; 20</option>
        </select>
      );
    } else if (column === 'is_etf') {
      // ETF filter
      return (
        <select
          className="form-select"
          value={filters[column] || ''}
          onChange={(e) => handleFilterChange(column, e.target.value === '' ? '' : parseInt(e.target.value))}
        >
          <option value="">Any</option>
          <option value="1">ETF Only</option>
          <option value="0">Stocks Only</option>
        </select>
      );
    } else if (column === 'sma_50_200') {
      // SMA position filter
      return (
        <select
          className="form-select"
          value={filters[column] || ''}
          onChange={(e) => handleFilterChange(column, e.target.value)}
        >
          <option value="">Any</option>
          <option value="above">50d Above 200d sma</option>
          <option value="below">50d Below 200d sma</option>
        </select>
      );
    } else if (['returnOnEquityTTM', 'grossProfitMarginTTM', 'netProfitMarginTTM'].includes(column)) {
      // Margin/ROE percentage filter
      return (
        <select
          className="form-select"
          value={filters[column] || ''}
          onChange={(e) => handleFilterChange(column, e.target.value)}
        >
          <option value="">Any</option>
          <option value="lt_0">Negative(&lt;0)</option>
          <option value="gt_0">Positive(&gt;0)</option>
          <option value="gt_5">Above 5%</option>
          <option value="gt_10">Above 10%</option>
          <option value="gt_15">Above 15%</option>
          <option value="gt_20">Above 20%</option>
          <option value="gt_25">Above 25%</option>
        </select>
      );
    } else if (column === 'priceSalesRatioTTM') {
      // Price to Sales Ratio filter
      return (
        <select
          className="form-select"
          value={filters[column] || ''}
          onChange={(e) => handleFilterChange(column, e.target.value)}
        >
          <option value="">Any</option>
          <option value="lt_1">Under 1</option>
          <option value="lt_2">Under 2</option>
          <option value="lt_3">Under 3</option>
          <option value="lt_4">Under 4</option>
          <option value="lt_5">Under 5</option>
          <option value="lt_6">Under 6</option>
          <option value="lt_7">Under 7</option>
          <option value="lt_8">Under 8</option>
          <option value="lt_9">Under 9</option>
          <option value="lt_10">Under 10</option>
          <option value="gt_1">Over 1</option>
          <option value="gt_2">Over 2</option>
          <option value="gt_3">Over 3</option>
          <option value="gt_4">Over 4</option>
          <option value="gt_5">Over 5</option>
          <option value="gt_6">Over 6</option>
          <option value="gt_7">Over 7</option>
          <option value="gt_8">Over 8</option>
          <option value="gt_9">Over 9</option>
        </select>
      );
    } else if (column === 'priceEarningsRatioTTM') {
      // Price to Earnings Ratio filter
      return (
        <select
          className="form-select"
          value={filters[column] || ''}
          onChange={(e) => handleFilterChange(column, e.target.value)}
        >
          <option value="">Any</option>
          <option value="gt_0">Profitable(&gt;0)</option>
          <option value="lt_15">Low(&lt;15)</option>
          <option value="lt_30">Moderate(&lt;30)</option>
          <option value="gt_50">High(&gt;50)</option>
        </select>
      );
    } else if (column === 'priceEarningsToGrowthRatioTTM') {
      // Price to Earnings to Growth Ratio filter
      return (
        <select
          className="form-select"
          value={filters[column] || ''}
          onChange={(e) => handleFilterChange(column, e.target.value)}
        >
          <option value="">Any</option>
          <option value="lt_1">Low(&lt;1)</option>
          <option value="gt_2">High(&gt;2)</option>
        </select>
      );
    } else if (column === 'debtRatioTTM') {
      // Debt Ratio filter
      return (
        <select
          className="form-select"
          value={filters[column] || ''}
          onChange={(e) => handleFilterChange(column, e.target.value)}
        >
          <option value="">Any</option>
          <option value="gt_0.3">High(&gt;0.3)</option>
          <option value="gt_0.5">Very High(&gt;0.5)</option>
          <option value="lt_0.3">Low(&lt;0.3)</option>
          <option value="lt_0.1">Very Low(&lt;0.1)</option>
        </select>
      );
    } else if (column === 'piotroskiScore') {
      // Piotroski Score filter
      return (
        <select
          className="form-select"
          value={filters[column] || ''}
          onChange={(e) => handleFilterChange(column, e.target.value)}
        >
          <option value="">Any</option>
          <option value="good">Good(7,8,9)</option>
          <option value="moderate">Moderate(4,5,6)</option>
          <option value="poor">Poor(3,2,1,0)</option>
        </select>
      );
    } else if (['revenue_1yr_growth', 'revenue_3yr_growth', 'forecast_next_year_revenue_growth_rate',
                'gross_profit_1yr_growth', 'gross_profit_3yr_growth', 'forecast_next_year_profit_growth_rate',
                'eps_cagr'].includes(column)) {
      // Growth percentage filter
      return (
        <select
          className="form-select"
          value={filters[column] || ''}
          onChange={(e) => handleFilterChange(column, e.target.value)}
        >
          <option value="">Any</option>
          <option value="lt_0">Negative(&lt;0)</option>
          <option value="gt_0">Positive(&gt;0)</option>
          <option value="gt_5">Above 5%</option>
          <option value="gt_10">Above 10%</option>
          <option value="gt_15">Above 15%</option>
          <option value="gt_20">Above 20%</option>
          <option value="gt_25">Above 25%</option>
          <option value="gt_30">Above 30%</option>
        </select>
      );
    } else if (column === 'price_to_dcf_ratio') {
      // Price to DCF Ratio filter
      return (
        <select
          className="form-select"
          value={filters[column] || ''}
          onChange={(e) => handleFilterChange(column, e.target.value)}
        >
          <option value="">Any</option>
          <option value="lt_1">Undervalued(&lt;1.0)</option>
          <option value="gt_1">Overvalued(&gt;1.0)</option>
        </select>
      );
    } else if (column === 'rule_of_40') {
      // Rule of 40 filter
      return (
        <select
          className="form-select"
          value={filters[column] || ''}
          onChange={(e) => handleFilterChange(column, e.target.value)}
        >
          <option value="">Any</option>
          <option value="gt_20">Over 20</option>
          <option value="gt_40">Over 40</option>
          <option value="gt_60">Over 60</option>
          <option value="gt_80">Over 80</option>
        </select>
      );
    } else if (['pcnt_from_sma200', 'pcnt_from_sma100'].includes(column)) {
      // Range filter (min-max)
      const rangeValues = filters[column] || [0, 100];
      
      return (
        <div className="flex items-center space-x-2">
          <input
            type="number"
            className="form-input w-20"
            placeholder="Min"
            value={rangeValues[0]}
            onChange={(e) => handleRangeFilterChange(column, 0, e.target.value)}
          />
          <span>-</span>
          <input
            type="number"
            className="form-input w-20"
            placeholder="Max"
            value={rangeValues[1]}
            onChange={(e) => handleRangeFilterChange(column, 1, e.target.value)}
          />
        </div>
      );
    } else if (['sma50_slope', 'sma200_slope'].includes(column)) {
      // Boolean filter (positive/negative)
      return (
        <select
          className="form-select"
          value={filters[column] || ''}
          onChange={(e) => handleFilterChange(column, e.target.value === '' ? '' : parseInt(e.target.value))}
        >
          <option value="">Any</option>
          <option value="1">Positive</option>
          <option value="0">Negative</option>
        </select>
      );
    } else if (['symbol', 'companyName'].includes(column)) {
      // Text search filter
      return (
        <input
          type="text"
          className="form-input"
          placeholder={column === 'symbol' ? 'Enter symbols: AAPL, MSFT, GOOGL...' : 'Search name...'}
          value={filters[column] || ''}
          onChange={(e) => handleFilterChange(column, e.target.value)}
        />
      );
    } else {
      // Default text filter
      return (
        <input
          type="text"
          className="form-input"
          placeholder="Filter value..."
          value={filters[column] || ''}
          onChange={(e) => handleFilterChange(column, e.target.value)}
        />
      );
    }
  };

  // Format column name for display
  const formatColumnName = (column) => {
    // Custom labels for specific columns
    if (column === 'symbol_list') {
      return 'Symbols';
    }
    if (column === 'sma_50_200') {
      return '50d Sma Relative To 200d Sma';
    }
    
    return column
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/TTM/g, ' TTM') // Add space before TTM
      .replace(/([0-9]+)/g, ' $1') // Add space before numbers
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <div
      className={`fixed top-0 left-0 h-full bg-white shadow-lg z-40 w-full md:w-80 transform transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } overflow-y-auto`}
    >
      {/* Header */}
      <div className="sticky top-0 bg-white z-10 border-b border-gray-200">
        <div className="flex justify-between items-center p-4">
          <h2 className="text-xl font-semibold text-gray-900">Filters</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
            aria-label="Close"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 overflow-x-auto">
          <button
            className={`flex-1 py-3 px-2 text-xs font-medium whitespace-nowrap ${
              activeTab === 'basic'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('basic')}
          >
            Basic
          </button>
          <button
            className={`flex-1 py-3 px-2 text-xs font-medium whitespace-nowrap ${
              activeTab === 'technical'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('technical')}
          >
            Technical
          </button>
          <button
            className={`flex-1 py-3 px-2 text-xs font-medium whitespace-nowrap ${
              activeTab === 'financial'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('financial')}
          >
            Financial
          </button>
          <button
            className={`flex-1 py-3 px-2 text-xs font-medium whitespace-nowrap ${
              activeTab === 'growth'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('growth')}
          >
            Growth
          </button>
        </div>
      </div>

      {/* Filter Content */}
      <div className="p-4">
        {/* Presets Toggle */}
        <button
          onClick={() => setShowPresets(!showPresets)}
          className="flex items-center justify-between w-full mb-4 p-2 bg-gray-100 rounded-md text-gray-700 hover:bg-gray-200"
        >
          <span className="font-medium">Saved Filter Presets</span>
          <svg
            className={`h-5 w-5 transform transition-transform ${
              showPresets ? 'rotate-180' : ''
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {/* Presets List */}
        {showPresets && (
          <div className="mb-6 space-y-2">
            {presets && presets.length > 0 ? (
              presets.map((preset) => (
                <div
                  key={preset.id}
                  className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-md"
                >
                  <span className="text-sm font-medium text-gray-700">
                    {preset.name}
                  </span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onLoadPreset(preset)}
                      className="text-blue-600 hover:text-blue-800"
                      title="Load preset"
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => onDeletePreset(preset.id)}
                      className="text-red-600 hover:text-red-800"
                      title="Delete preset"
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 text-center py-2">
                No saved presets yet
              </p>
            )}
          </div>
        )}

        {/* Filter Fields */}
        <div className="space-y-4">
          {filterGroups[activeTab].map((column) => (
            <div key={column} className="form-group">
              <label className="form-label text-sm font-medium text-gray-700">
                {formatColumnName(column)}
              </label>
              {renderFilterInput(column)}
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 space-y-2">
        <div className="flex space-x-2">
          <button
            onClick={handleClearFilters}
            className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Clear All
          </button>
          <button
            onClick={handleApplyFilters}
            className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Apply Filters
          </button>
        </div>
        <button
          onClick={onSavePreset}
          className="w-full py-2 px-4 border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50"
        >
          Save Current Filters
        </button>
      </div>
    </div>
  );
};

export default FilterMenu;
